import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnChanges,
    SimpleChanges,
    AfterViewInit,
    ChangeDetectorRef,
} from '@angular/core';
import { Betriebskontakt } from './betriebskontakt';

export interface BetriebskontaktCardConfig {
    showActions?: boolean;
    showMenu?: boolean;
    showFavorit?: boolean;
    showBadge?: boolean;
    compact?: boolean;
    selectedMode?: boolean; // true = nur Entfernen, false = volles Menü
    showHinzufuegen?: boolean; // true = zeigt "Zu Kontakten hinzufügen" Button
    istKontaktBereitsVorhanden?: boolean; // true = Kontakt ist bereits in eigenen Kontakten, Button wird ausgeblendet
    ladeHinzufuegen?: boolean; // true = Button ist disabled während des Hinzufügens
    canEdit?: boolean; // true = Bearbeiten erlaubt
}

@Component({
    selector: 'kiene-betriebskontakt-card',
    templateUrl: './betriebskontakt-card.component.html',
    styleUrls: ['./betriebskontakt-card.component.scss'],
})
export class BetriebskontaktCardComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() kontakt!: Betriebskontakt;
    @Input() config: BetriebskontaktCardConfig = {
        showActions: true,
        showMenu: true,
        showFavorit: true,
        showBadge: true,
        compact: false,
        selectedMode: false,
        canEdit: true,
    };

    // Direkter Input für showHinzufuegen (überschreibt config.showHinzufuegen)
    @Input() showHinzufuegen?: boolean;

    // Flag um zu prüfen ob Card bereit ist
    cardReady = false;

    @Output() bearbeiten = new EventEmitter<Betriebskontakt>();
    @Output() toggleFavorit = new EventEmitter<Betriebskontakt>();
    @Output() entfernen = new EventEmitter<Betriebskontakt>();
    @Output() sendMail = new EventEmitter<Betriebskontakt>();
    @Output() callPhone = new EventEmitter<string>();
    @Output() hinzufuegen = new EventEmitter<Betriebskontakt>(); // Neuer Event für "Zu Kontakten hinzufügen"

    constructor(private cdr: ChangeDetectorRef) {}

    /**
     * Gibt die Badge-Farbe basierend auf der Betriebsart zurück
     */
    getBetriebsartBadgeColor(betriebsartId?: number): string {
        switch (betriebsartId) {
            case 9001: // Schlachthöfe
                return '#f44336'; // Rot
            case 9002: // Händler
                return '#2196f3'; // Blau
            default: // Landwirte und andere
                return '#4caf50'; // Grün
        }
    }

    /**
     * Gibt den Badge-Text basierend auf der Betriebsart zurück
     */
    getBetriebsartBadgeText(betriebsartId?: number): string {
        switch (betriebsartId) {
            case 9001:
                return 'Schlachthof';
            case 9002:
                return 'Händler';
            default:
                return 'Landwirt';
        }
    }

    /**
     * Gibt die vollständige Adresse als String zurück
     */
    getVollAdresse(kontakt: Betriebskontakt): string {
        const teile = [];
        if (kontakt.strasse) teile.push(kontakt.strasse);
        if (kontakt.plz) teile.push(kontakt.plz);
        if (kontakt.ort) teile.push(kontakt.ort);
        if (kontakt.ortsteil) teile.push(`(${kontakt.ortsteil})`);
        return teile.join(' ');
    }

    /**
     * Gibt die erste verfügbare Telefonnummer zurück
     */
    getTelefonNummer(kontakt: Betriebskontakt): string | null {
        return kontakt.telefon_1 || kontakt.telefon_2 || kontakt.mobil_1 || kontakt.mobil_2 || null;
    }

    /**
     * Event-Handler für Bearbeiten
     */
    onBearbeiten(): void {
        this.bearbeiten.emit(this.kontakt);
    }

    /**
     * Event-Handler für Favorit togglen
     */
    onToggleFavorit(): void {
        this.toggleFavorit.emit(this.kontakt);
    }

    /**
     * Event-Handler für Entfernen
     */
    onEntfernen(): void {
        this.entfernen.emit(this.kontakt);
    }

    /**
     * Event-Handler für E-Mail senden
     */
    onSendMail(): void {
        this.sendMail.emit(this.kontakt);
    }

    /**
     * Event-Handler für Anruf
     */
    onCallPhone(nummer: string): void {
        this.callPhone.emit(nummer);
    }

    /**
     * Event-Handler für "Zu Kontakten hinzufügen"
     */
    onHinzufuegen(): void {
        // Prüfe ob Card bereit ist
        if (!this.cardReady) {
            return;
        }

        this.hinzufuegen.emit(this.kontakt);
    }

    ngOnInit(): void {
        // Explizite Change Detection nach Initialisierung
        setTimeout(() => {
            this.cdr.detectChanges();
        }, 50);
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Explizite Change Detection nach Änderungen
        setTimeout(() => {
            this.cdr.detectChanges();
        }, 10);
    }

    ngAfterViewInit(): void {
        // Zusätzliche Change Detection nach View-Initialisierung
        setTimeout(() => {
            this.cdr.detectChanges();
            this.cardReady = true;
        }, 100);
    }
}
